// src/pages/organization/ExamBuilderPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../../utils/httpRequest";
import { toast } from "react-toastify";
import { Eye, LogOut, Plus, FolderOpen, Upload } from "lucide-react";
import DraftsPickerModal from "../../components/exam-builder/DraftsPickerModal";
// builder components
import CreateSectionModal from "../../components/exam-builder/CreateSectionModal";
import EditSectionModal from "../../components/exam-builder/EditSectionModal";
import SectionCard from "../../components/exam-builder/SectionCard";
import ExamMetaBar from "../../components/exam-builder/ExamMetaBar";
import ItemPickerModal from "../../components/exam-builder/ItemPickerModal";
import { createExamDraft, updateExam } from "../../services/api/exams";
import SectionItemsModal from "../../components/exam-builder/SectionItemsModal";
import { Link } from "react-router-dom";
import MediaPickerModal from "../../components/exam-builder/MediaPickerModal";
import { useNavigate } from "react-router-dom";
import PreviewMenuButton from "../../components/exam-builder/PreviewMenuButton";
import { BASE_URL } from "../../config";
import { Download, FileSpreadsheet } from "lucide-react";
import ExcelImportModal from "../../components/exam-builder/ExcelImportModal";
import { downloadExamExcelTemplate } from "../../services/api/examExcel";

const ExamBuilderPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  // sections state
  const [sections, setSections] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [examId, setExamId] = useState(null);
  // edit section state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [targetSectionIndex, setTargetSectionIndex] = useState(null);
  const [isDraftsOpen, setIsDraftsOpen] = useState(false);
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [publishingExam, setPublishingExam] = useState(false);
  const [isExcelImportOpen, setIsExcelImportOpen] = useState(false);
  // exam meta state
  const [meta, setMeta] = useState({
    title: "",
    description: "",
    categoryId: "",
    timeMode: "per_section",
    totalTime: "",
    // فیلدهای جدید
    meta: {
      headerColor: "#7e22ce",
      imageSize: "0",
      languageMode: "auto",
    },
  });

  console.log(meta);

  const [savingDraft, setSavingDraft] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);

  // categories (level 3)
  const [level3Categories, setLevel3Categories] = useState([]);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [manageIndex, setManageIndex] = useState(null);

  const navigate = useNavigate();

  const openManageItems = async (section, index) => {
    if (section?.type === "ai") {
      toast.info("برای سکشن هوش مصنوعی، مدیریت سوالات دستی وجود ندارد.");
      return;
    }
    try {
      await ensureDraft(); // حتماً draft و examId داشته باشیم
      setManageIndex(index);
      setIsManageOpen(true);
    } catch {}
  };

  // fetch level-3 categories
  useEffect(() => {
    (async () => {
      try {
        const res = await httpRequest(
          "GET",
          "/category",
          {},
          { "x-access-token": user.token },
        );
        const raw = res?.data?.data || res?.data || [];
        setLevel3Categories(raw.filter((c) => c.level === 3));
      } catch (e) {
        setLevel3Categories([]);
      }
    })();
  }, [user?.token]);

  // This function converts the UI state into the correct API payload format.
  // This function converts the UI state into the correct API payload format.
  const toApiSections = (uiSections = []) =>
    (uiSections || []).map((s, idx) => {
      const str = (v) => (typeof v === "string" ? v : "");
      const title = {
        fa:
          str(s?.title_fa) ||
          (typeof s?.title === "string" ? s.title : str(s?.title?.fa)) ||
          `سکشن ${idx + 1}`,
        en:
          str(s?.title_en) ||
          (typeof s?.title === "object" ? str(s?.title?.en) : ""),
      };

      const descFa =
        str(s?.description_fa) ||
        (typeof s?.description === "string"
          ? s.description
          : str(s?.description?.fa));
      const descEn =
        str(s?.description_en) ||
        (typeof s?.description === "object" ? str(s?.description?.en) : "");
      const description =
        descFa || descEn ? { fa: descFa, en: descEn } : undefined;

      const timeLimitSec =
        typeof s?.timeLimitSec === "number"
          ? s.timeLimitSec
          : s?.timeLimitSec
            ? Number(s.timeLimitSec)
            : undefined;

      const audioSrc = s?.audioFile || {};
      const audioFile =
        audioSrc &&
        (audioSrc.url ||
          typeof audioSrc.maxPlays === "number" ||
          typeof audioSrc.allowPause === "boolean")
          ? {
              url: str(audioSrc.url),
              maxPlays:
                typeof audioSrc.maxPlays === "number" ? audioSrc.maxPlays : 1,
              allowPause: !!audioSrc.allowPause,
            }
          : undefined;

      // ✅ layout سکشن
      const layout = s?.meta?.ui?.layout === "two_pane" ? "two_pane" : "single";

      const items = (Array.isArray(s?.items) ? s.items : []).map((it, i) => ({
        item: it?.item?._id || it?.item || null,
        order: typeof it?.order === "number" ? it.order : i + 1,
        points:
          typeof it?.points === "number"
            ? it.points
            : typeof it?.item?.points === "number"
              ? it.item.points
              : undefined,
        required: it?.required !== false,
        anchorId: it?.anchorId || undefined,
      }));

      // ✅ parts + passageHtml
      const parts =
        Array.isArray(s?.parts) && s.parts.length
          ? s.parts.map((p) => ({
              id: p.id,
              title: p.title,
              promptHtml:
                typeof p.promptHtml === "string" ? p.promptHtml : undefined,
              passageHtml:
                typeof p.passageHtml === "string" ? p.passageHtml : undefined, // ✅
              blocks: Array.isArray(p.blocks)
                ? p.blocks.map((b) => ({
                    id: b.id,
                    html: b.html,
                    from: typeof b.from === "number" ? b.from : undefined,
                    to: typeof b.to === "number" ? b.to : undefined,
                  }))
                : undefined,
            }))
          : undefined;

      return {
        type: s?.type || "classic",
        ai: s?.type === "ai" ? s?.ai : undefined,
        title,
        description,
        timeLimitSec,
        shuffleItems: !!s?.shuffleItems,
        audioFile,
        displayMode:
          s?.displayMode || s?.navigation?.displayMode || "one_per_page",
        meta: { ui: { layout } }, // ✅
        parts,
        items,
      };
    });

  // This function converts the API response into the correct UI state format.
  // This function converts the API response into the correct UI state format.
  const fromApiSections = (apiSections = []) =>
    (apiSections || []).map((s, idx) => {
      const title =
        typeof s?.title === "string"
          ? { fa: s.title, en: "" }
          : { fa: s?.title?.fa ?? `سکشن ${idx + 1}`, en: s?.title?.en ?? "" };

      const description =
        typeof s?.description === "string"
          ? { fa: s.description, en: "" }
          : { fa: s?.description?.fa ?? "", en: s?.description?.en ?? "" };

      const audioFile = {
        url: s?.audioFile?.url ?? "",
        maxPlays:
          typeof s?.audioFile?.maxPlays === "number" ? s.audioFile.maxPlays : 1,
        allowPause: !!s?.audioFile?.allowPause,
      };

      // ✅ layout را از meta.ui.layout بخوان
      const layout = s?.meta?.ui?.layout === "two_pane" ? "two_pane" : "single";

      // ✅ parts + passageHtml را برگردان
      const parts = Array.isArray(s?.parts)
        ? s.parts.map((p) => ({
            id: p.id,
            title: p.title,
            promptHtml: typeof p.promptHtml === "string" ? p.promptHtml : "",
            passageHtml: typeof p.passageHtml === "string" ? p.passageHtml : "", // ✅
            blocks: Array.isArray(p.blocks) ? p.blocks : [],
          }))
        : [];

      const items = (s?.items || []).map((it, i) => ({
        item: it?.item || null,
        order: typeof it?.order === "number" ? it.order : i + 1,
        points:
          typeof it?.points === "number"
            ? it.points
            : typeof it?.item?.points === "number"
              ? it.item.points
              : undefined,
        required: it?.required !== false,
        anchorId: it?.anchorId,
        _id: it?._id,
      }));

      return {
        type: s?.type || "classic",
        ai: s?.ai,
        _id: s?._id,
        title,
        description,
        timeLimitSec:
          typeof s?.timeLimitSec === "number" ? s.timeLimitSec : undefined,
        shuffleItems: !!s?.shuffleItems,
        displayMode: s?.displayMode || "one_per_page",
        audioFile,
        meta: { ui: { layout } }, // ✅ در state نگه‌دار
        parts,
        items,
      };
    });

  const openPreview = async (mode) => {
    try {
      // حتماً درفت بساز/به‌روز کن تا پیش‌نمایش دادهٔ تازه داشته باشه
      const id = await ensureDraft();
      // همگام‌سازی سریع سکشن‌ها (اختیاری ولی مفید)
      await updateExam(user.token, id, {
        sections: toApiSections(sections),
        meta: meta.meta,
      });
      const url = `/exam/preview/${id}?mode=practice`;
      navigate(url);
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };
  const loadDraftById = async (id) => {
    if (!id) return;
    setLoadingDraft(true);
    try {
      const res = await httpRequest(
        "GET",
        `/exam/${id}`,
        {},
        { "x-access-token": user.token },
      );
      const ex = res?.data?.data || res?.data;
      if (!ex?._id) throw new Error(res?.message || "NOT_FOUND");

      // Correctly setting the meta state from the API response
      setMeta({
        title: ex?.title?.fa || ex?.titleFa || "",
        description: ex?.description?.fa || ex?.descFa || "",
        categoryId: ex?.category?._id || ex?.category || "",
        timeMode: ex?.timeMode || "",
        totalTime: ex?.totalTime || "",
        meta: ex?.meta,
      });

      // Using the corrected helper function to parse sections
      setSections(fromApiSections(ex?.sections || []));

      setExamId(ex._id);
      toast.success(t("exam_builder.draft_loaded"));
    } catch (err) {
      toast.error(err?.message || t("exam_builder.load_failed"));
    } finally {
      setLoadingDraft(false);
    }
  };
  const ensureDraft = async () => {
    if (examId) return examId;

    if (!meta.title?.trim() || !meta.categoryId) {
      toast.error(t("exam_builder.toasts.fill_meta_first"));
      throw new Error("meta missing");
    }

    const res = await createExamDraft(user.token, {
      categoryId: meta.categoryId,
      titleFa: meta.title,
      descFa: meta.description,
      timeMode: meta.timeMode,
      totalTime: meta.totalTime,
    });

    const id = res?.data?._id || res?.data?.data?._id || res?.data?.id;

    if (!id) {
      toast.error(res?.message || t("common.unknown_error"));
      throw new Error("draft create failed");
    }

    setExamId(id);
    return id;
  };

  const addItemsToSection = async (pickedItems) => {
    // 1) ساخت نسخهٔ جدیدِ sections
    const nextSections = (() => {
      const clone = [...sections];
      const sec = { ...(clone[targetSectionIndex] || { items: [] }) };
      const curr = sec.items || [];
      const additions = (pickedItems || []).map((it) => ({
        item: it._id,
        points: typeof it.points === "number" ? it.points : 1,
        required: true,
      }));
      sec.items = [...curr, ...additions];
      clone[targetSectionIndex] = sec;
      return clone;
    })();

    // 2) optimistic UI
    setSections(nextSections);

    // 3) Autosave روی سرور
    try {
      const id = await ensureDraft();
      const payload = { sections: toApiSections(nextSections) };
      const res = await updateExam(user.token, id, payload);
      if (!(res?.status === 200 || res?.data?._id)) {
        toast.error(res?.message || t("common.unknown_error"));
      }
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };
  // handlers
  const handleCreateSection = (newSection) => {
    setSections((prev) => [...prev, newSection]);
  };

  const openEdit = (section, index) => {
    setEditingSection(section);
    setEditingIndex(index);
    setIsEditOpen(true);
  };

  const updateSectionAtIndex = (data, index) => {
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...data } : s)),
    );
  };

  const deleteSectionAtIndex = (index) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const buildPayload = () => ({
    category: meta.categoryId,
    title: { fa: meta.title, en: "" },
    description: meta.description
      ? { fa: meta.description, en: "" }
      : undefined,
    isPublished: false,
    sections: toApiSections(sections),
    timeMode: meta.timeMode,
    totalTime: meta.totalTime,
    meta: meta.meta,
  });

  const handlePublish = async () => {
    // اعتبارسنجی پایه (مثل handleSave)
    if (!meta.title?.trim()) {
      toast.error(t("exam_builder.toasts.exam_title_required"));
      return;
    }
    if (!meta.categoryId) {
      toast.error(t("exam_builder.toasts.exam_category_required"));
      return;
    }
    if (sections.length === 0) {
      toast.error(t("exam_builder.toasts.no_sections_to_save"));
      return;
    }

    setPublishingExam(true);
    try {
      // اگر هنوز ID نداریم، اول ذخیرهٔ درفت:
      if (!examId) {
        await handleSave(); // این خودش examId را ست می‌کند (طبق کد فعلی‌ات)
      }
      const currentId = examId || null;
      if (!currentId) {
        toast.error(t("common.unknown_error"));
        return;
      }

      // درخواست انتشار (فقط isPublished را true کن)
      const res = await httpRequest(
        "PATCH",
        `/exam/${currentId}/publish`,
        { isPublished: true },
        {
          "x-access-token": user.token,
        },
      );

      const ok = res?.status === 200 || res?.data?._id || res?.data?.data?._id;

      if (ok) {
        setMeta((m) => ({ ...m, isPublished: true }));
        toast.success(t("exam_builder.toasts.published") || "آزمون منتشر شد");
      } else {
        toast.error(res?.message || t("common.unknown_error"));
      }
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    } finally {
      setPublishingExam(false);
    }
  };

  const handleSave = async () => {
    // اعتبارسنجی پایه
    if (!meta.title?.trim()) {
      toast.error(t("exam_builder.toasts.exam_title_required"));
      return;
    }
    if (!meta.categoryId) {
      toast.error(t("exam_builder.toasts.exam_category_required"));
      return;
    }
    if (sections.length === 0) {
      toast.error(t("exam_builder.toasts.no_sections_to_save"));
      return;
    }

    setSavingDraft(true);
    try {
      const payload = buildPayload();
      if (examId) {
        // آپدیت روی همان درفت
        const res = await updateExam(user.token, examId, payload);
        if (res?.status === 200 || res?.data?._id) {
          toast.success(
            t("exam_builder.toasts.draft_updated") || "تغییرات ذخیره شد",
          );
        } else {
          toast.error(res?.message || t("common.unknown_error"));
        }
      } else {
        // اگر هنوز درفتی ساخته نشده، ایجاد کن
        const res = await httpRequest("POST", "/exam", payload, {
          "x-access-token": user.token,
        });
        const id = res?.data?._id || res?.data?.data?._id || res?.data?.id;
        if (id) {
          setExamId(id);
          toast.success(t("exam_builder.toasts.draft_saved"));
        } else {
          toast.error(res?.message || t("common.unknown_error"));
        }
      }
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSaveAs = async (newTitleFa) => {
    // ✅ نام از اینلاین‌اینپوت می‌آید؛ نه از window.prompt
    const titleFa = (newTitleFa || "").trim();

    // اعتبارسنجی پایه
    if (!meta.title?.trim()) {
      toast.error(t("exam_builder.toasts.exam_title_required"));
      return;
    }
    if (!meta.categoryId) {
      toast.error(t("exam_builder.toasts.exam_category_required"));
      return;
    }
    if (sections.length === 0) {
      toast.error(t("exam_builder.toasts.no_sections_to_save"));
      return;
    }
    if (!titleFa) {
      toast.error("عنوان جدید نمی‌تواند خالی باشد");
      return;
    }

    setSavingDraft(true);
    try {
      const payload = { ...buildPayload(), title: { fa: titleFa, en: "" } };
      // همیشه ایجاد جدید
      const res = await httpRequest("POST", "/exam", payload, {
        "x-access-token": user.token,
      });

      const newId = res?.data?._id || res?.data?.data?._id || res?.data?.id;

      if (newId) {
        // سوییچ context به درفت جدید
        setExamId(newId);
        setMeta((m) => ({ ...m, title: titleFa }));
        toast.success("نسخهٔ جدید ساخته شد");
      } else {
        toast.error(res?.message || t("common.unknown_error"));
      }
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    } finally {
      setSavingDraft(false);
    }
  };

  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-200 rounded-[16px]`;

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("exam_builder.page_title")}</title>
      </Helmet>

      <main className={mainSectionClass}>
        {/* Header + MetaBar */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">
              {t("exam_builder.page_header")}
            </h1>
            <div className="flex gap-2 ">
              <button
                onClick={() => setIsMediaOpen(true)}
                className="bg-teal-600 rounded-lg px-4 py-2 text-white hover:bg-teal-700 inline-flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {t("exam_builder.media_library")}
              </button>
              <PreviewMenuButton onPreview={openPreview} />
              {/* <button
                onClick={goPreview}
                className="bg-amber-600 rounded-lg px-4 py-2 text-white hover:bg-amber-700 inline-flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                {t("exam_builder.preview")}
              </button> */}
              <button
                onClick={async () => {
                  try {
                    const id = await ensureDraft();
                    // دانلود مستقیم
                    window.location.href = `${BASE_URL}/exam/${id}/export.xlsx`;
                  } catch (e) {
                    toast.error("Export failed");
                  }
                }}
                className="bg-slate-700 rounded-lg px-4 py-2 text-white hover:bg-slate-800 inline-flex items-center gap-2"
              >
                {t("exam_builder.download_excel")}
              </button>
              <button
                onClick={() => setIsExcelImportOpen(true)}
                className="bg-emerald-600 rounded-lg px-4 py-2 text-white hover:bg-emerald-700 inline-flex items-center gap-2"
              >
                <FileSpreadsheet className="w-4 h-4" />
                {t("exam_builder.upload_excel")}
              </button>
              <button
                onClick={() => setIsDraftsOpen(true)}
                className="bg-blue-600 rounded-lg px-4 py-2 text-white hover:bg-blue-700 inline-flex items-center gap-2"
              >
                <FolderOpen className="w-4 h-4" />
                {t("exam_builder.open_draft")}
              </button>
              <button
                onClick={() => setIsCreateOpen(true)}
                className="bg-purple-600 rounded-lg px-4 py-2 text-white hover:bg-purple-700 inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t("exam_builder.create_section")}
              </button>
              <Link
                to={"/organization/exams"}
                className="bg-red-600 rounded-lg px-4 py-2 text-white hover:bg-red-700 inline-flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                {t("exam_builder.exit")}
              </Link>
            </div>
          </div>

          <ExamMetaBar
            meta={meta}
            setMeta={setMeta}
            level3Categories={level3Categories}
            onSave={handleSave} // ← PUT یا POST بر اساس examId
            onSaveAs={handleSaveAs} // ← همیشه POST با عنوان جدید
            saving={savingDraft}
            onPublish={handlePublish} // ← جدید
            publishing={publishingExam}
          />
        </div>

        {/* Sections list */}
        {sections.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sections.map((s, idx) => (
              <SectionCard
                key={idx}
                section={s}
                index={idx}
                onEdit={openEdit}
                onDelete={(_, index) => deleteSectionAtIndex(index)}
                onManageItems={openManageItems}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            {t("exam_builder.no_sections")}
          </p>
        )}
      </main>

      {/* Create Section */}
      <CreateSectionModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleCreateSection}
      />

      {/* Edit Section */}
      <EditSectionModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        section={editingSection}
        index={editingIndex}
        onUpdate={updateSectionAtIndex}
      />

      <MediaPickerModal
        isOpen={isMediaOpen}
        onClose={() => setIsMediaOpen(false)}
        token={user.token}
        onPick={(assets) => {
          // اینجا هر جا لازم شد می‌تونی از فایل‌ها استفاده کنی (مثلا در فرم سوالات تصویری/صوتی)
          toast.success(t("media_picker.selected_n", { n: assets.length }));
          setIsMediaOpen(false);
        }}
      />

      <ItemPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        token={user.token}
        onPick={addItemsToSection}
        categoryId={meta.categoryId}
        examId={examId}
      />

      <SectionItemsModal
        isOpen={isManageOpen}
        onClose={() => setIsManageOpen(false)}
        token={user.token}
        examId={examId}
        sectionIndex={manageIndex}
        sections={sections}
        setSections={setSections}
        ensureDraft={ensureDraft}
        toApiSections={toApiSections}
        categoryId={meta.categoryId}
      />

      <DraftsPickerModal
        isOpen={isDraftsOpen}
        onClose={() => setIsDraftsOpen(false)}
        token={user.token}
        onPick={(ex) => {
          setIsDraftsOpen(false);
          // حتماً دیتیل تازه بگیر؛ لیست ممکنه آیتم‌ها را ندهد
          const id = ex?._id || ex;
          loadDraftById(id);
        }}
      />

      <ExcelImportModal
        isOpen={isExcelImportOpen}
        onClose={() => setIsExcelImportOpen(false)}
        token={user.token}
      />
    </div>
  );
};

export default ExamBuilderPage;
